import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Copy, RotateCcw, XCircle, Eye, CheckCircle, Send, Clock } from 'lucide-react';
import { formatDateTime } from '../lib/format';
import { toast } from 'sonner';
import api from '../services/api';

export const DeliveryHistory = ({ documentType, documentId, onRefresh, refreshKey = 0 }) => {
  // GOLDEN BUILD RULE: Delivery records are append-only audit entries. Always show
  // every send attempt independently and never infer or overwrite its open status.
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const loadDeliveries = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const response = await api.get(`/document-delivery?document_type=${documentType}&document_id=${documentId}`);
      setDeliveries(response.data);
      setLastLoadedAt(new Date());
    } catch (err) {
      console.error('Failed to load delivery history:', err);
      toast.error('Failed to load delivery history');
    } finally {
      setLoading(false);
    }
  }, [documentId, documentType]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries, refreshKey]);

  const copyLink = (token) => {
    const link = `${window.location.origin}/view/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const handleRevoke = async (deliveryId) => {
    try {
      await api.post(`/document-delivery/${deliveryId}/revoke`);
      toast.success('Link revoked successfully');
      await loadDeliveries();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke link');
      console.error('Revoke error:', err);
    } finally {
      setRevokeTarget(null);
    }
  };

  const getStatusBadge = (delivery) => {
    if (delivery.token_revoked_at) {
      return <Badge className="bg-slate-100 text-slate-800"><XCircle className="w-3 h-3 mr-1" />Revoked</Badge>;
    }
    if (delivery.delivery_status === 'accepted') {
      return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
    }
    if (delivery.delivery_status === 'declined') {
      return <Badge className="bg-rose-100 text-rose-800"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
    }
    if (delivery.delivery_status === 'failed') {
      return <Badge className="bg-rose-100 text-rose-800"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    }
    if (delivery.viewed_at) {
      return <Badge className="bg-blue-100 text-blue-800"><Eye className="w-3 h-3 mr-1" />Viewed</Badge>;
    }
    if (delivery.sent_at) {
      return <Badge className="bg-amber-100 text-amber-800"><Send className="w-3 h-3 mr-1" />Sent</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const isActive = (delivery) => {
    return !delivery.token_revoked_at && !delivery.responded_at;
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg font-semibold text-slate-900">Delivery History</CardTitle>
            <Button size="sm" variant="outline" onClick={loadDeliveries} disabled={loading}>
              <RotateCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (deliveries.length === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm" data-testid="delivery-history-empty">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg font-semibold text-slate-900">Delivery History</CardTitle>
            <Button size="sm" variant="outline" onClick={loadDeliveries}>
              <RotateCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">No delivery records yet. Send this document to create the first delivery.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-slate-200 shadow-sm" data-testid="delivery-history">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">Delivery History</CardTitle>
              {lastLoadedAt && (
                <p className="text-xs font-normal text-slate-500 mt-1">Updated {formatDateTime(lastLoadedAt)}</p>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={loadDeliveries} disabled={loading}>
              <RotateCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Attempt</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Viewed</TableHead>
                <TableHead>Response</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((delivery, index) => (
                <TableRow key={delivery.id}>
                  <TableCell className="text-sm font-medium text-slate-900">
                    #{deliveries.length - index}
                  </TableCell>
                  <TableCell className="text-sm text-slate-900">
                    {delivery.sent_at ? formatDateTime(delivery.sent_at) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-900 capitalize">
                      {delivery.document_type}
                    </div>
                    <div className="text-xs text-slate-500">
                      {delivery.subject || '-'}
                    </div>
                    {delivery.email_template_key && (
                      <div className="text-xs text-slate-400">
                        Template: {delivery.email_template_key}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-900">{delivery.recipient_to}</div>
                    {delivery.recipient_cc && (
                      <div className="text-xs text-slate-500">CC: {delivery.recipient_cc}</div>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(delivery)}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {delivery.viewed_at ? formatDateTime(delivery.viewed_at) : 'Not opened'}
                  </TableCell>
                  <TableCell>
                    {delivery.responded_at ? (
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {delivery.response_action === 'accepted' ? 'Accepted' : 'Declined'}
                        </div>
                        {delivery.response_name && (
                          <div className="text-xs text-slate-500">by {delivery.response_name}</div>
                        )}
                        {delivery.response_comment && (
                          <div className="text-xs text-slate-500 italic mt-1">"{delivery.response_comment}"</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {isActive(delivery) && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyLink(delivery.secure_token)}
                            title="Copy link"
                            data-testid={`copy-link-${delivery.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRevokeTarget(delivery.id)}
                            title="Revoke link"
                            data-testid={`revoke-link-${delivery.id}`}
                          >
                            <XCircle className="w-4 h-4 text-rose-600" />
                          </Button>
                        </>
                      )}
                      {delivery.token_revoked_at && (
                        <span className="text-xs text-slate-400 px-2">Revoked</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Delivery Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this link? The recipient will no longer be able to access the document using this link.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRevoke(revokeTarget)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-revoke-button"
            >
              Revoke Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
